import React, { useEffect, useState } from 'react';
import { Crown } from 'lucide-react';
import { listTiers, compareTiers } from '../services/tierService';

const TIER_COLORS: Record<string, string> = {
  professional: 'border-blue-500 bg-blue-50',
  advanced: 'border-purple-500 bg-purple-50',
  premium: 'border-amber-500 bg-amber-50',
};

const TIER_BADGE: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

export default function TiersPage() {
  const [tiers, setTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [compare, setCompare] = useState<any>(null);

  useEffect(() => {
    listTiers().then(setTiers).catch(() => setTiers([])).finally(() => setLoading(false));
  }, []);

  const handleCompare = async (code1: string, code2: string) => {
    try {
      const all = await listTiers();
      const t1 = all.find(t => t.code === code1);
      const t2 = all.find(t => t.code === code2);
      if (!t1 || !t2) { setCompare(null); return; }
      const diff = compareTiers(t1, t2);
      setCompare({ tiers: [t1, t2], featureDiff: {}, limitDiff: diff });
    } catch { setCompare(null); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="theme-grid p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Subscription Tiers</h1>
        <p className="text-gray-500 mt-1">Professional / Advanced / Premium tier definitions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {tiers.map((t) => (
          <div key={t._id} className={`rounded-xl border-2 p-6 ${TIER_COLORS[t.code] || 'border-gray-200 bg-white'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Crown className="h-5 w-5 text-gray-600" />
              <span className={`px-2 py-0.5 text-xs font-bold rounded-full uppercase ${TIER_BADGE[t.code] || 'bg-gray-100 text-gray-600'}`}>{t.code}</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">{t.name}</h2>
            <p className="text-sm text-gray-500 mt-1">{t.tagline}</p>

            <div className="mt-4 text-2xl font-bold text-gray-900">
              ${t.pricing?.baseMonthlyMin}–${t.pricing?.baseMonthlyMax}<span className="text-sm font-normal text-gray-500">/mo</span>
            </div>

            <div className="mt-4 space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase">Limits</h3>
              <div className="text-sm text-gray-700">
                <div>Users: <strong>{t.limits?.maxUsers ?? '∞'}</strong></div>
                <div>Parcels: <strong>{t.limits?.maxParcels?.toLocaleString() ?? '∞'}</strong></div>
                <div>Storage: <strong>{t.limits?.maxStorageGB ? `${t.limits.maxStorageGB} GB` : '∞'}</strong></div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase">Features</h3>
              <div className="text-sm space-y-1">
                {Object.entries(t.features || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className={`h-4 w-4 rounded-full flex items-center justify-center text-xs ${val ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {val ? '✓' : '—'}
                    </span>
                    <span className={val ? 'text-gray-700' : 'text-gray-400'}>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 space-y-1">
              <h3 className="text-xs font-semibold text-gray-500 uppercase">Support</h3>
              <div className="text-sm text-gray-700 capitalize">{t.support?.level}</div>
              {t.support?.sla && <div className="text-xs text-gray-500">SLA: {t.support.sla}</div>}
            </div>

            <div className="mt-4 text-xs text-gray-400">
              Infrastructure: {t.infrastructure?.clusterType}
              {t.infrastructure?.clusterSize && ` (${t.infrastructure.clusterSize})`}
            </div>
          </div>
        ))}
      </div>

      {tiers.length >= 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Compare Tiers</h2>
          <div className="flex gap-3 mb-4">
            {tiers.length >= 2 && (
              <>
                <button onClick={() => handleCompare('professional', 'advanced')} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">Professional vs Advanced</button>
                <button onClick={() => handleCompare('advanced', 'premium')} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">Advanced vs Premium</button>
                <button onClick={() => handleCompare('professional', 'premium')} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">Professional vs Premium</button>
              </>
            )}
          </div>
          {compare && (
            <div className="space-y-4">
              {Object.keys(compare.featureDiff || {}).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Feature Differences</h3>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-1 text-gray-500">Feature</th>{compare.tiers.map((t: any) => <th key={t.code} className="text-left py-1 text-gray-500 capitalize">{t.code}</th>)}</tr></thead>
                    <tbody>
                      {Object.entries(compare.featureDiff).map(([key, vals]: [string, any]) => (
                        <tr key={key} className="border-b border-gray-50">
                          <td className="py-1.5 text-gray-700">{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                          {compare.tiers.map((t: any) => (
                            <td key={t.code} className="py-1.5">
                              <span className={vals[t.code] ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                {vals[t.code] ? '✓ Yes' : '✗ No'}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {Object.keys(compare.limitDiff || {}).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Limit Differences</h3>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-1 text-gray-500">Limit</th>{compare.tiers.map((t: any) => <th key={t.code} className="text-left py-1 text-gray-500 capitalize">{t.code}</th>)}</tr></thead>
                    <tbody>
                      {Object.entries(compare.limitDiff).map(([key, vals]: [string, any]) => (
                        <tr key={key} className="border-b border-gray-50">
                          <td className="py-1.5 text-gray-700">{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                          {compare.tiers.map((t: any) => (
                            <td key={t.code} className="py-1.5 text-gray-700">{vals[t.code] ?? '∞'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
